import { selectCurrentHotelId } from 'accessors'
import DragAndDropUploader from 'components/ReusableComponents/DragAndDropUploader'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import FilePreview from 'components/ReusableComponents/Messaging/ConversationBubble/FilePreview'
import {
  MediaUploadFormatsInfo,
  MediaUploadMaxSizeHint
} from 'features/ManageCampaigns/MediaUploadHints'
import RecentMediaDialog from 'features/ManageCampaigns/RecentMediaDialog'
import Right from 'features/ManageCampaigns/RecentMediaDialog/Right'
import { getExtensionFromUrl, getImgUrlFromCDN } from 'features/ManageCampaigns/utils/campaignHelpers'
import { getAcceptedFileTypesLabel, maxFileSize, maxFileSizeLabel } from 'features/Messaging/fileUploadConfig'
import useFirstRender from 'hooks/useFirstRender'
import useLocalStorage from 'hooks/useLocalStorageState'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import dashboardApi from 'services/api/dashboard'

import styles from './MediaUploader.module.scss'

const MediaUploader = ({
  setSelectedMedia,
  selectedMedia,
  removeSelectedMedia,
  hasTitle = true,
  acceptedFileTypes = ['image/*'],
  previewCount = 4,
  mediaTag
}) => {
  const [isRecentMediaDialogOpen, setIsRecentMediaDialogOpen] = useState(false)

  const hotelId = useSelector(selectCurrentHotelId)

  const [recentFiles, setRecentFiles] = useLocalStorage(
    `mediaList-${mediaTag}-${hotelId}`,
    []
  )

  const [lastPositionToken, setLastPositionToken] = useLocalStorage(
    `mediaToken-${mediaTag}-${hotelId}`,
    null
  )

  const [hasAllFiles, setHasAllFiles] = useState(false)

  const { addToast } = useToasts()

  function handleFileSizeExceeded() {
    addToast(`The selected file exceeds the maximum allowed size of ${maxFileSizeLabel}.`, {
      appearance: 'error'
    })
  }

  function handleInvalidFileType() {
    addToast(
      `This file type is not supported. Supported formats: ${getAcceptedFileTypesLabel(
        acceptedFileTypes
      )}.`,
      { appearance: 'error' }
    )
  }

  function getMediaList() {
    dashboardApi.getMediaList(mediaTag, lastPositionToken).then(mediaList => {
      const files = mediaList?.files || []

      if (files?.length) {
        const normalizedMediaList = files.reduce((prev, curr) => {
          return { ...prev, [curr.fileName]: curr }
        }, {})

        setRecentFiles(m => ({ ...m, ...normalizedMediaList }))

        if (lastPositionToken && lastPositionToken === mediaList?.lastPositionToken) {
          setHasAllFiles(true)
        }
      }
    })
  }

  useEffect(() => {
    if (recentFiles && Object.values(recentFiles).length > 0) {
      const lastFileName =
        Object.values(recentFiles)[Object.values(recentFiles).length - 1].fileName

      if (lastFileName.startsWith('20')) {
        setLastPositionToken(lastFileName)
      }
    }
  }, [recentFiles, setLastPositionToken])

  useFirstRender(() => {
    getMediaList()
  })

  return (
    <>
      {isRecentMediaDialogOpen && (
        <RecentMediaDialog
          handleHideDialog={() => setIsRecentMediaDialogOpen(false)}
          setSelectedMedia={setSelectedMedia}
          recentFiles={recentFiles}
          setRecentFiles={setRecentFiles}
          getMoreMedia={getMediaList}
          hasAllFiles={hasAllFiles}
          mediaTag={mediaTag}
        />
      )}
      {hasTitle && (
        <div className={styles.editTextFormTitleRow}>
          <p className={styles.editTextFormTitle}>Attachments</p>
          <MediaUploadFormatsInfo acceptedFileTypes={acceptedFileTypes} />
        </div>
      )}
      {!selectedMedia && <MediaUploadMaxSizeHint />}
      <div className={styles.dragAndDropHolder}>
        {selectedMedia ? (
          <div className={styles.selectedMediaHolder}>
            <div className={styles.selectedMedia}>
              <FilePreview
                extension={getExtensionFromUrl(selectedMedia)}
                src={getImgUrlFromCDN(selectedMedia, mediaTag, hotelId)}
                clickable={false}
                // showFileName={true}
              />
              <p className={styles.selectedFileName}>
                {selectedMedia.replace(/\d{14}__swaypay__/, '').replace(/(c|a)\//, '')}
              </p>
            </div>
            <button
              className={styles.selectedMediaRemoveButton}
              onClick={removeSelectedMedia}
            >
              <CrossIcon fill="white" />
            </button>
          </div>
        ) : (
          <DragAndDropUploader
            url="/media"
            acceptedFileTypes={acceptedFileTypes}
            maxFileSize={maxFileSize}
            maxFiles={1}
            onFileSizeExceeded={handleFileSizeExceeded}
            onInvalidFileType={handleInvalidFileType}
            onLoad={res => {
              const data = JSON.parse(res)
              setSelectedMedia(data?.result?.temporaryUrl?.fileName)
            }}
            onData={formData => {
              formData.append('tag', mediaTag)
              return formData
            }}
            labelIdle="
        <p class='filepond-p-micro'>drop a file here</p>
    <span class='filepond-or-micro'>or</span> <span class='filepond--label-action-micro'>Select file</span>"
          />
        )}
      </div>
      {Object.values(recentFiles).length > 0 && (
        <div>
          <p className={styles.recentFilesTitle}>Select from recent files</p>
          <div className={styles.thumbnailHolder}>
            {Object.values(recentFiles)
              .reverse()
              .slice(0, previewCount)
              .map(file => (
                <>
                  <div
                    className={styles.thumbnail}
                    style={{
                      border:
                        file.fileName === selectedMedia
                          ? '2px solid var(--linkBlue)'
                          : '2px solid transparent'
                    }}
                    onClick={() => setSelectedMedia(file.fileName)}
                  >
                    <div className={styles.fileHolder}>
                      <FilePreview
                        extension={getExtensionFromUrl(file.fileName)}
                        src={getImgUrlFromCDN(file.fileName, mediaTag, hotelId)}
                        clickable={false}
                        className={styles.thumbnailInner}
                      />
                      <span className={styles.fileName}>
                        {file.fileName.replace(/\d{14}__swaypay__/, '')}
                      </span>
                    </div>
                  </div>
                </>
              ))}
            {Object.values(recentFiles).length > previewCount && (
              <button
                className={styles.seeMoreButton}
                onClick={() => setIsRecentMediaDialogOpen(true)}
              >
                <Right width={20} height={30} />
                <span>See more</span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

MediaUploader.propTypes = {
  handleUploadedFile: PropTypes.func,
  mediaTag: PropTypes.oneOf(['c', 'a']) // [C]ampaign or [A]nnouncement
}

export default MediaUploader
